/* Kemp Beauty homepage refresh.
   1. <kb-video-player>: YouTube-style in-place player. A preview image with a
      play button; clicking swaps in the real <video> (with controls) inside
      the same box. The <video> lives in a <template> until then, so no video
      bytes are fetched on page load. Only one player plays at a time.
   2. One-time entrance reveal for [data-kb-reveal], armed only when the
      visitor has not asked for reduced motion. */
(function () {
  if (!customElements.get('kb-video-player')) {
    class KbVideoPlayer extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        this.frame = this.querySelector('.kb-video__frame');
        this.button = this.querySelector('[data-kb-video-play]');
        this.template = this.querySelector('template');
        this.lazyPosterFrame();
        if (!this.frame || !this.button || !this.template) return;
        this.button.addEventListener('click', () => this.play());
      }

      // A still frame from the video stands in for a poster image. Its src is
      // set only when it comes within ~one screen of the viewport.
      lazyPosterFrame() {
        const still = this.querySelector('video[data-kb-poster-src]');
        if (!still) return;
        const load = () => {
          still.src = still.dataset.kbPosterSrc;
          still.removeAttribute('data-kb-poster-src');
        };
        if (!('IntersectionObserver' in window)) return load();
        const io = new IntersectionObserver((entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          io.disconnect();
          load();
        }, { rootMargin: '600px 0px' });
        io.observe(still);
      }

      play() {
        if (this.video) return;
        this.frame.appendChild(this.template.content.cloneNode(true));
        this.video = this.frame.querySelector('video.kb-video__media') || this.frame.querySelector('video:not(.kb-poster-video)');
        if (!this.video) return;
        this.video.classList.add('kb-video__media');
        // Pause any other Kemp player that is running.
        this.video.addEventListener('play', () => {
          document.querySelectorAll('kb-video-player').forEach((p) => {
            if (p !== this && p.video && !p.video.paused) p.video.pause();
          });
        });
        // Remove the preview and the overlay button; the video's own
        // controls take over (keyboard focus moves onto the video).
        this.frame.querySelectorAll('.kb-video__poster').forEach((el) => {
          if (el.tagName === 'VIDEO') { el.removeAttribute('src'); el.load(); }
          el.remove();
        });
        this.button.remove();
        this.classList.add('is-playing');
        // Started by a deliberate click on play, so sound is expected.
        const playing = this.video.play();
        if (playing && playing.catch) playing.catch(() => {});
        this.video.focus({ preventScroll: true });
      }
    }
    customElements.define('kb-video-player', KbVideoPlayer);

    // Any [data-kb-video-target="<player id>"] button starts that player,
    // e.g. the "Watch the film" link above the About Us founder film.
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-kb-video-target]');
      if (!trigger) return;
      const player = document.getElementById(trigger.dataset.kbVideoTarget);
      if (!player || typeof player.play !== 'function') return;
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      player.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'center' });
      player.play();
    });
  }

  // ---- Athlete gallery: filmstrip arrows + reel tiles ----
  const initGameDay = (root) => {
    root.querySelectorAll('[data-kbg-strip]:not([data-kbg-ready])').forEach((strip) => {
      strip.setAttribute('data-kbg-ready', '');
      const section = strip.closest('.kbg');
      const prev = section && section.querySelector('[data-kbg-prev]');
      const next = section && section.querySelector('[data-kbg-next]');
      const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // Portrait row: one click moves a full "page" of cards; otherwise 80% of the view.
      const step = () => {
        if (section && section.classList.contains('kbg--row')) {
          return strip.clientWidth + (parseFloat(getComputedStyle(strip).columnGap) || 0);
        }
        return Math.max(240, strip.clientWidth * 0.8);
      };
      const update = () => {
        const max = strip.scrollWidth - strip.clientWidth - 2;
        if (prev) prev.disabled = strip.scrollLeft <= 2;
        if (next) next.disabled = strip.scrollLeft >= max;
      };
      const go = (dir) => strip.scrollBy({ left: dir * step(), behavior: reduce() ? 'auto' : 'smooth' });
      if (prev) prev.addEventListener('click', () => go(-1));
      if (next) next.addEventListener('click', () => go(1));
      strip.addEventListener('scroll', update, { passive: true });
      strip.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
      });
      window.addEventListener('resize', update);
      update();
    });

    // Tap (or click) a photo to ease it in; tap again, or another photo, to reset.
    root.querySelectorAll('[data-kbg-tapzoom]:not([data-kbg-zoom-ready])').forEach((grid) => {
      grid.setAttribute('data-kbg-zoom-ready', '');
      grid.addEventListener('click', (e) => {
        const tile = e.target.closest('.kbg__tile:not(.kbg__reel)');
        if (!tile) return;
        const on = !tile.classList.contains('is-zoomed');
        grid.querySelectorAll('.kbg__tile.is-zoomed').forEach((t) => t.classList.remove('is-zoomed'));
        tile.classList.toggle('is-zoomed', on);
      });
    });

    root.querySelectorAll('[data-kbg-reel]:not([data-kbg-ready])').forEach((tile) => {
      tile.setAttribute('data-kbg-ready', '');
      const video = tile.querySelector('[data-kbg-reel-video]');
      const play = tile.querySelector('[data-kbg-reel-play]');
      if (!video || !play) return;
      // Preview frame: attach the file only when the tile is near the viewport.
      const load = () => { if (!video.getAttribute('src')) { video.src = video.dataset.src; video.preload = 'metadata'; } };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) { io.disconnect(); load(); }
        }, { rootMargin: '400px 0px' });
        io.observe(tile);
      } else { load(); }
      play.addEventListener('click', () => {
        load();
        video.currentTime = 0;
        video.muted = false;
        video.controls = true;
        tile.classList.add('is-playing');
        const p = video.play();
        if (p && p.catch) p.catch(() => {});
        video.focus({ preventScroll: true });
      });
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initGameDay(document));
  } else {
    initGameDay(document);
  }
  document.addEventListener('shopify:section:load', (e) => initGameDay(e.target));

  // ---- Hero carousel: crossfade, counter, prev/next, pause, swipe ----
  const initHeroCarousel = (root) => {
    root.querySelectorAll('[data-kbc]:not([data-kbc-ready])').forEach((el) => {
      el.setAttribute('data-kbc-ready', '');
      const slides = Array.from(el.querySelectorAll('[data-kbc-slide]'));
      if (slides.length < 2) return;
      const track = el.querySelector('[data-kbc-track]');
      const current = el.querySelector('[data-kbc-current]');
      const bar = el.querySelector('[data-kbc-bar]');
      const toggle = el.querySelector('[data-kbc-toggle]');
      const interval = (parseFloat(el.dataset.interval) || 7) * 1000;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      const inEditor = window.Shopify && window.Shopify.designMode;
      let index = 0;
      let timer = null;
      let userPaused = reduce.matches || inEditor || el.dataset.autoplay !== 'true';
      let hoverPaused = false;

      const restartBar = () => {
        if (!bar) return;
        bar.style.animation = 'none';
        void bar.offsetWidth; // restart the CSS progress animation
        bar.style.animation = '';
      };
      const show = (i, announce) => {
        index = (i + slides.length) % slides.length;
        slides.forEach((sl, n) => sl.classList.toggle('is-active', n === index));
        if (current) current.textContent = String(index + 1).padStart(2, '0');
        el.dataset.activeType = slides[index].classList.contains('kbc__slide--kit') ? 'kit' : 'athlete';
        el.querySelectorAll('[data-kbc-dot]').forEach((d, n) => d.setAttribute('aria-current', n === index ? 'true' : 'false'));
        track.setAttribute('aria-live', announce ? 'polite' : 'off');
        restartBar();
      };
      const stop = () => { clearInterval(timer); timer = null; };
      const sync = () => {
        stop();
        const playing = !userPaused && !hoverPaused && !document.hidden;
        el.classList.toggle('is-playing', !userPaused);
        el.classList.toggle('is-paused', !userPaused && !playing);
        if (toggle) toggle.textContent = userPaused ? 'Play slideshow' : 'Pause slideshow';
        if (playing) timer = setInterval(() => show(index + 1, false), interval);
      };

      const prev = el.querySelector('[data-kbc-prev]');
      const next = el.querySelector('[data-kbc-next]');
      if (prev) prev.addEventListener('click', () => { show(index - 1, true); sync(); });
      if (next) next.addEventListener('click', () => { show(index + 1, true); sync(); });
      if (toggle) toggle.addEventListener('click', () => { userPaused = !userPaused; restartBar(); sync(); });
      el.querySelectorAll('[data-kbc-dot]').forEach((d) => d.addEventListener('click', () => { show(Number(d.dataset.kbcDot), true); sync(); }));
      // The hero fills most of the screen, so the pointer is almost always over
      // it: pausing on hover would stop the carousel for nearly everyone.
      // Pause only while a keyboard user is focused inside it.
      el.addEventListener('focusin', (e) => {
        if (e.target.matches && e.target.matches(':focus-visible')) { hoverPaused = true; sync(); }
      });
      el.addEventListener('focusout', (e) => { if (!el.contains(e.relatedTarget)) { hoverPaused = false; sync(); } });
      document.addEventListener('visibilitychange', sync);
      reduce.addEventListener && reduce.addEventListener('change', (e) => { if (e.matches) { userPaused = true; sync(); } });

      // Swipe (touch/pen): a clear horizontal drag changes slide.
      let startX = null, startY = null;
      track.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse') { startX = e.clientX; startY = e.clientY; } });
      track.addEventListener('pointerup', (e) => {
        if (startX === null) return;
        const dx = e.clientX - startX, dy = e.clientY - startY;
        startX = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) { show(index + (dx < 0 ? 1 : -1), true); sync(); }
      });

      // Theme editor: selecting a slide block shows it.
      el.addEventListener('shopify:block:select', (e) => {
        const n = slides.indexOf(e.target.closest('[data-kbc-slide]'));
        if (n > -1) { show(n, false); userPaused = true; sync(); }
      });

      show(0, false);
      sync();
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initHeroCarousel(document));
  } else {
    initHeroCarousel(document);
  }
  document.addEventListener('shopify:section:load', (e) => initHeroCarousel(e.target));

  // ---- Brand film player and Home reel: muted autoplay in view, custom controls ----
  const initFilmPlayer = (root) => {
    root.querySelectorAll('[data-kbp]:not([data-kbp-ready])').forEach((el) => {
      el.setAttribute('data-kbp-ready', '');
      const video = el.querySelector('[data-kbp-video]');
      const src = el.dataset.src;
      if (!video || !src) return;
      const screen = el.querySelector('.kbp__screen');
      const prompt = el.querySelector('[data-kbp-prompt]');
      const playBtn = el.querySelector('[data-kbp-play]');
      const muteBtn = el.querySelector('[data-kbp-mute]');
      const fullBtn = el.querySelector('[data-kbp-full]');
      const progress = el.querySelector('[data-kbp-progress]');
      const time = el.querySelector('[data-kbp-time]');
      // Ambient reels (silent loops) autoplay even for reduced-motion visitors.
      const ambient = el.hasAttribute('data-kbp-ambient');
      const reduce = !ambient && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const inEditor = window.Shopify && window.Shopify.designMode;
      let userPaused = false;
      let visible = false;

      const attach = () => { if (!video.getAttribute('src')) { video.src = src; video.preload = 'metadata'; } };
      const fmt = (t) => { t = Math.max(0, Math.floor(t || 0)); return Math.floor(t / 60) + ':' + String(t % 60).padStart(2, '0'); };
      const sync = () => {
        el.classList.toggle('is-paused', video.paused);
        el.classList.toggle('is-unmuted', !video.muted);
        if (playBtn) playBtn.setAttribute('aria-label', video.paused ? 'Play' : 'Pause');
        if (muteBtn) muteBtn.setAttribute('aria-label', video.muted ? 'Turn sound on' : 'Mute');
      };
      const start = (withSound) => {
        attach();
        if (withSound) video.muted = false;
        el.classList.add('is-started');
        // Film: hand over to the browser's own controls once it is playing.
        if (el.hasAttribute('data-kbp-native') && !video.controls) { video.controls = true; video.removeAttribute('tabindex'); }
        const p = video.play();
        if (p && p.catch) p.catch(() => { video.muted = true; video.play().catch(() => {}); });
      };

      // Attach the file only when the player is about a screen away.
      const near = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { attach(); near.disconnect(); }
      }, { rootMargin: '400px 0px' });
      near.observe(el);

      // Silent autoplay while at least half the film is on screen.
      const seen = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          visible = e.isIntersecting;
          if (visible && !userPaused && !reduce && !inEditor) start(false);
          else if (!visible && !video.paused) video.pause();
        });
      }, { threshold: ambient ? 0.3 : 0.5 });
      seen.observe(screen);

      // Ambient reels: if the phone blocked autoplay, a tap on the screen starts it.
      if (ambient) screen.addEventListener('click', (e) => {
        if (e.target.closest('button') || !video.paused) return;
        userPaused = false; start(false); sync();
      });

      if (prompt) prompt.addEventListener('click', () => {
        if (!el.classList.contains('is-started') || video.paused) { userPaused = false; start(true); }
        else { video.muted = false; }
        sync();
      });
      if (playBtn) playBtn.addEventListener('click', () => {
        if (video.paused) { userPaused = false; start(false); } else { userPaused = true; video.pause(); }
      });
      if (muteBtn) muteBtn.addEventListener('click', () => { video.muted = !video.muted; sync(); });
      if (fullBtn) fullBtn.addEventListener('click', () => {
        if (document.fullscreenElement) { document.exitFullscreen(); return; }
        if (screen.requestFullscreen) screen.requestFullscreen();
        else if (video.webkitEnterFullscreen) video.webkitEnterFullscreen();
      });
      if (progress) {
        progress.addEventListener('input', () => {
          if (video.duration) video.currentTime = (progress.value / 1000) * video.duration;
        });
      }
      video.addEventListener('timeupdate', () => {
        if (progress && video.duration) progress.value = Math.round((video.currentTime / video.duration) * 1000);
        if (time) time.textContent = fmt(video.currentTime) + ' / ' + fmt(video.duration);
      });
      ['play', 'pause', 'volumechange', 'loadedmetadata'].forEach((ev) => video.addEventListener(ev, sync));
      sync();
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initFilmPlayer(document));
  } else {
    initFilmPlayer(document);
  }
  document.addEventListener('shopify:section:load', (e) => initFilmPlayer(e.target));

  if (window.kbRevealInit) return;
  window.kbRevealInit = true;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const inEditor = window.Shopify && window.Shopify.designMode;
  if (reduce.matches || inEditor || !('IntersectionObserver' in window)) return;

  const root = document.documentElement;
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-revealed');
      observer.unobserve(entry.target);
    });
  }, { threshold: 0 });

  const arm = () => {
    const targets = document.querySelectorAll('[data-kb-reveal]:not(.is-revealed)');
    if (!targets.length) return;
    // Anything already on screen is marked revealed *before* motion is armed,
    // so it never fades out and back in. Only content still below the fold
    // gets the entrance, and it is revealed as soon as any part of it shows.
    targets.forEach((el) => {
      if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add('is-revealed');
    });
    root.classList.add('kb-motion');
    targets.forEach((el) => {
      if (!el.classList.contains('is-revealed')) observer.observe(el);
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arm);
  } else {
    arm();
  }

  // If the visitor switches reduced motion on mid-visit, show everything.
  reduce.addEventListener && reduce.addEventListener('change', (e) => {
    if (!e.matches) return;
    document.querySelectorAll('[data-kb-reveal]').forEach((el) => el.classList.add('is-revealed'));
    observer.disconnect();
  });
})();
